import { trackEvent } from "@koaform/analytics";
import { useEffect, useState } from "react";

import { Share } from "./Share.js";

const RENDERER_URL =
  import.meta.env.VITE_RENDERER_URL ?? "http://localhost:5052";

import { qrMatrix } from "./qr.js";

/** Rendered from a locally-computed matrix — see qr.ts for why not an image. */
const QrCode = ({ text, size = 168 }: { text: string; size?: number }) => {
  const matrix = qrMatrix(text);
  if (!matrix) {
    return (
      <p className="text-[12.5px] text-faint">
        This link is too long for a QR code.
      </p>
    );
  }
  const n = matrix.length;
  const quiet = 2;
  const total = n + quiet * 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${total} ${total}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label="QR code for this form"
      className="rounded-lg border border-line-strong bg-white p-1"
    >
      <rect width={total} height={total} fill="#ffffff" />
      {matrix.flatMap((row, y) =>
        row.map((on, x) =>
          on ? (
            <rect
              key={`${x}-${y}`}
              x={x + quiet}
              y={y + quiet}
              width={1}
              height={1}
              fill="#0c1a2e"
            />
          ) : null,
        ),
      )}
    </svg>
  );
};

/**
 * Renders the QR to a PNG blob at print-usable size.
 *
 * Drawn from the matrix rather than rasterising the on-screen SVG: an SVG
 * sized for the dialog would come out at ~168px, which is too coarse once
 * someone puts it on a poster. Each module becomes a fixed block of pixels,
 * so the output is crisp at any size, with a quiet zone — scanners need the
 * white margin, and a QR cropped flush to its edge often will not read.
 */
const qrPngBlob = async (text: string): Promise<Blob | null> => {
  const matrix = qrMatrix(text);
  if (!matrix) return null;

  const quiet = 4;
  const scale = 12;
  const modules = matrix.length + quiet * 2;
  const size = modules * scale;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#0c1a2e";
  matrix.forEach((row, y) =>
    row.forEach((on, x) => {
      if (on) {
        ctx.fillRect((x + quiet) * scale, (y + quiet) * scale, scale, scale);
      }
    }),
  );

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
};

const slugifyName = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "form";

const downloadQr = async (url: string, title: string) => {
  const blob = await qrPngBlob(url);
  if (!blob) return;
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `${slugifyName(title)}-qr.png`;
  link.click();
  URL.revokeObjectURL(href);
};

const copyQr = async (
  url: string,
  setState: (s: "ok" | "fail" | null) => void,
) => {
  try {
    const blob = await qrPngBlob(url);
    // Firefox has no image clipboard support at all, and Safari needs the
    // ClipboardItem constructed synchronously — both land here as a failure,
    // which is why the button falls back to pointing at Download.
    if (!blob || !navigator.clipboard?.write) throw new Error("unsupported");
    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
    setState("ok");
  } catch {
    setState("fail");
  }
  setTimeout(() => setState(null), 2600);
};

type Tab = "link" | "social" | "qr" | "embed";

const TABS: { id: Tab; label: string }[] = [
  { id: "link", label: "Link" },
  { id: "social", label: "Social" },
  { id: "qr", label: "QR code" },
  { id: "embed", label: "Embed" },
];

/**
 * Everything you can do with a published form's URL, in one place.
 *
 * Koalendar puts link, social, QR and embed behind one Share button, so
 * anyone moving between the two products finds the same shape here.
 */
export const ShareDialog = ({
  slug,
  title,
  onClose,
}: {
  slug: string;
  title: string;
  onClose: () => void;
}) => {
  const [tab, setTab] = useState<Tab>("link");
  // Which sharing route people actually use decides what to build next.
  useEffect(() => {
    trackEvent("Share opened");
  }, []);
  const [copied, setCopied] = useState(false);
  const [qrCopied, setQrCopied] = useState<"ok" | "fail" | null>(null);
  const url = `${RENDERER_URL}/r/${slug}`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      trackEvent("Share link copied");
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Needs a secure context; the field stays selectable regardless.
    }
  };

  const encoded = encodeURIComponent(url);
  const subject = encodeURIComponent(title);
  const blurb = encodeURIComponent(`${title} — `);

  const SOCIALS = [
    { label: "Email", href: `mailto:?subject=${subject}&body=${blurb}${encoded}` }, // prettier-ignore
    { label: "WhatsApp", href: `https://wa.me/?text=${blurb}${encoded}` },
    { label: "X", href: `https://twitter.com/intent/tweet?text=${blurb}&url=${encoded}` }, // prettier-ignore
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}` }, // prettier-ignore
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` }, // prettier-ignore
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share this form"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="animate-fade fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-ink/45 p-6 backdrop-blur-[3px]"
    >
      <div className="animate-rise mt-[6vh] w-[560px] max-w-full rounded-[20px] bg-surface p-7 shadow-[0_30px_70px_-20px_rgba(12,26,46,0.5)]">
        <div className="mb-1 flex items-start justify-between gap-4">
          <h2 className="m-0 text-[21px] font-extrabold tracking-[-0.025em]">
            Share “{title}”
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-[30px] w-[30px] flex-none rounded-lg bg-line text-[15px] text-muted hover:bg-line-input"
          >
            ×
          </button>
        </div>
        <p className="m-0 mb-5 text-[14px] text-muted">
          Anyone with the link can answer — no account needed.
        </p>

        <div
          role="tablist"
          className="mb-5 flex gap-1 rounded-[10px] bg-line p-1"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 rounded-[8px] px-2 py-2 text-[13px] font-semibold ${
                tab === t.id
                  ? "bg-surface text-ink shadow-sm"
                  : "text-muted hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "link" ? (
          <div className="flex gap-2">
            <input
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 rounded-[11px] border border-line-input bg-subtle p-[13px] font-mono text-[13px] text-body"
            />
            <button
              type="button"
              onClick={() => void copy()}
              className="flex-none rounded-[11px] bg-primary-600 px-5 text-sm font-bold text-white hover:bg-primary-700"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        ) : null}

        {tab === "social" ? (
          <div className="flex flex-wrap gap-2">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer noopener"
                className="rounded-[11px] border border-line-bold bg-surface px-4 py-2.5 text-sm font-semibold text-ink hover:border-primary-600 hover:text-ink"
              >
                {s.label}
              </a>
            ))}
          </div>
        ) : null}

        {tab === "qr" ? (
          <div className="flex flex-wrap items-start gap-5">
            <QrCode text={url} />
            <div className="min-w-[220px] flex-1">
              <p className="m-0 mb-4 text-[13.5px] leading-[1.55] text-muted">
                Point a phone camera at this to open the form. Drawn in your
                browser — the link is not sent anywhere to generate it.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void downloadQr(url, title)}
                  className="rounded-[10px] bg-primary-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-primary-700"
                >
                  Download PNG
                </button>
                <button
                  type="button"
                  onClick={() => void copyQr(url, setQrCopied)}
                  className="rounded-[10px] border border-line-bold bg-surface px-4 py-2.5 text-sm font-semibold text-ink hover:border-primary-600"
                >
                  {qrCopied === "ok"
                    ? "Copied"
                    : qrCopied === "fail"
                      ? "Use download"
                      : "Copy image"}
                </button>
              </div>
              {qrCopied === "fail" ? (
                <p className="mt-2 mb-0 text-[12.5px] leading-[1.5] text-faint">
                  This browser will not let a page write images to the clipboard
                  — download it instead.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {tab === "embed" ? <Share slug={slug} /> : null}
      </div>
    </div>
  );
};
